/**
 * Auth-related endpoints.
 *
 * Backed by the monolith routes (line numbers from the source):
 *   GET  /api/user-info              → returns { authenticated, user: { id, displayName, role, photoUrl, ... } }
 *   GET  /api/user-permissions/:id   → returns string[] of permission ids (non-admin only — admins are hardcoded)
 *   /auth/logout                     → window.location.href redirect target (not a fetch)
 */

import { apiGet } from '../client.js';
import { signOut } from '../../auth/msal.js';

/** Admin role grants this fixed permission set. */
export const ADMIN_PERMISSIONS = Object.freeze([
  'drive-management',
  'server-management',
  'user-management',
  'printer-management',
  'ad-sync',
  'policy-finder',
  'reporting',
  'group-management',
  'shared-mailboxes',
  'asset-management',
  'dylan-assistant',
  'log-analytics',
  'security-dashboard',
  'security-device-history',
  'security-shadow-ai',
  'security-shadow-ai-names',
  'tools',
  'network-dashboard',
  'sharepoint-management',
  'remote-tools',
  'cost-analysis',
  'automations',
  'compliance',
  'backups',
  'app-management',
  'claude-usage'
]);

/**
 * @typedef {Object} UserInfo
 * @property {string} id
 * @property {string} displayName
 * @property {string} role
 * @property {string} [photoUrl]
 * @property {string} [email]
 * @property {string} [upn]
 */

/**
 * @typedef {Object} UserInfoEnvelope
 * @property {boolean} authenticated
 * @property {UserInfo | null} user
 */

/**
 * Fetch the currently-signed-in user.
 * Throws ApiError on non-2xx; the API client redirects on 401 so this
 * call only resolves when a session is present.
 *
 * @returns {Promise<UserInfoEnvelope>}
 */
export async function fetchUserInfo() {
  return /** @type {UserInfoEnvelope} */ (await apiGet('/api/user-info'));
}

/**
 * Fetch a non-admin user's saved permission list. Admins MUST be
 * granted ADMIN_PERMISSIONS without calling this endpoint (matches the
 * monolith's loadUserPermissions branch logic).
 *
 * @param {string} userId
 * @returns {Promise<string[]>}
 */
export async function fetchUserPermissions(userId) {
  const result = await apiGet(`/api/user-permissions/${encodeURIComponent(userId)}`);
  return Array.isArray(result) ? result : [];
}

/**
 * Sign out. Retargeted for Omni Cloud (2026-07): the beta bounced through
 * a session-cookie `/auth/logout` redirect; Omni Cloud uses MSAL's
 * logoutRedirect instead. Name kept as `redirectToLogout` so call sites
 * (e.g. components/shell/TitleBar.jsx) needed no changes.
 */
export function redirectToLogout() {
  void signOut();
}
